type TextChunk = {
    readonly node: Text;
    readonly start: number;
};

// Keep contractions and hyphenated words together; surrounding punctuation
// remains a valid insertion boundary.
const WORD_PATTERN = /[\p{L}\p{M}\p{N}]+(?:['’\-‑][\p{L}\p{M}\p{N}]+)*/gu;

export function snapVariableDropToWord(
    editor: HTMLElement,
    range: Range,
    x: number,
    y: number,
): Range {
    const chunks: Array<TextChunk> = [];
    let text = '';
    let caret = -1;

    // Preview insertion splits text nodes. Read across those fragments so
    // a previous drag cannot turn the middle of a word into a drop target.
    function walk(node: Node) {
        if (node instanceof Text) {
            if (node === range.startContainer) {
                caret = text.length + range.startOffset;
            }
            chunks.push({ node, start: text.length });
            text += node.data;
            return;
        }
        if (!(node instanceof HTMLElement)) {
            return;
        }
        if (node.hasAttribute('data-variable-drop-preview')) {
            return;
        }
        if (node.hasAttribute('data-variable') || node.tagName === 'BR') {
            text += '\n';
            return;
        }
        const block = node !== editor && /^(DIV|P)$/.test(node.tagName);
        if (block) {
            text += '\n';
        }
        node.childNodes.forEach((child, index) => {
            if (node === range.startContainer && index === range.startOffset) {
                caret = text.length;
            }
            walk(child);
        });
        if (
            node === range.startContainer &&
            range.startOffset === node.childNodes.length
        ) {
            caret = text.length;
        }
        if (block) {
            text += '\n';
        }
    }
    walk(editor);

    const word = Array.from(text.matchAll(WORD_PATTERN)).find(
        (match) => match.index < caret && caret < match.index + match[0].length,
    );
    if (!word) {
        return range;
    }

    function rangeAt(offset: number): Range {
        const chunk = chunks.find(
            ({ node, start }) =>
                start <= offset && offset <= start + node.length,
        );
        const boundary = range.cloneRange();
        if (chunk) {
            boundary.setStart(chunk.node, offset - chunk.start);
        }
        boundary.collapse(true);
        return boundary;
    }

    const start = rangeAt(word.index);
    const endOffset = word.index + word[0].length;
    const end = rangeAt(endOffset);
    const startRect = start.getBoundingClientRect?.();
    const endRect = end.getBoundingClientRect?.();
    if (startRect?.height && endRect?.height) {
        const distance = (rect: DOMRect) =>
            (x - rect.left) ** 2 + (y - (rect.top + rect.height / 2)) ** 2;
        return distance(startRect) <= distance(endRect) ? start : end;
    }
    return caret - word.index <= endOffset - caret ? start : end;
}
